import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { billingMessages } from 'src/features/billing/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import { Link, useParams } from 'react-router-dom';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import { useGetAccountingRange } from 'src/features/accountingRanges/api/useGetAccountingRange';
import UpdateAccountingRangeFormModal from 'src/features/accountingRanges/components/forms/UpdateAccountingRangeFormModal';
import { Badge } from 'src/registry/ui/Badge';
import DeleteAccountingRangeFormModal from 'src/features/accountingRanges/components/forms/DeleteAccountingRangeFormModal';
import AccountingRangeDashboardTables from 'src/features/accountingRanges/components/tables/AccountingRangeDashboardTables';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const AccountingRangeDashboard = () => {
    const { formatMessage } = useIntl();
    const { rangeId = '' } = useParams();
    const permissionManageAccountingRanges = useHasPermissions([PERMISSION.MANAGE_ACCOUNTING_RANGES]);
    const permissionAccessBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(accountingRangesMessages.accountingRangeDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: accountingRangeData } = useGetAccountingRange({
        id: rangeId,
    });

    const dashboardBlockData = {
        section1: [
            {
                title: billingMessages.billingEntity,
                value: accountingRangeData?.billing_entity && (
                    <LinkWithIcon
                        to={`/billing-entity/${accountingRangeData?.billing_entity.id}`}
                        linkTitle={accountingRangeData?.billing_entity.name}
                        hasPermissions={permissionAccessBillingEntities}
                    />
                ),
            },
            {
                title: accountingRangesMessages.start,
                value: accountingRangeData?.start ?? '-',
            },
            {
                title: accountingRangesMessages.end,
                value: accountingRangeData?.end ?? '-',
            },
            {
                title: accountingRangesMessages.isManual,
                value: (
                    <Badge theme={accountingRangeData?.is_manual ? 'green' : 'red'}>
                        <FontAwesomeIcon icon={accountingRangeData?.is_manual ? ICON_MAP.CHECK : ICON_MAP.X_MARK} />
                        {formatMessage(accountingRangeData?.is_manual ? generalMessages.yes : generalMessages.no)}
                    </Badge>
                ),
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link">
                <Link to={`/accounting-ranges`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(accountingRangesMessages.backToAllAccountingRanges)}
                </Link>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {accountingRangeData?.name}
                        </SectionBlockTitle>

                        {permissionManageAccountingRanges && accountingRangeData && (
                            <div className="flex flex-row gap-2">
                                <UpdateAccountingRangeFormModal
                                    selectedAccountingRange={accountingRangeData}
                                    semantics={{
                                        entryName: formatMessage(accountingRangesMessages.range).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateAccountingRangeFormModal>
                                <DeleteAccountingRangeFormModal
                                    selectedAccountingRange={accountingRangeData}
                                    semantics={{
                                        entryName: formatMessage(accountingRangesMessages.range).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteAccountingRangeFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={0} />
                </SectionBlock>

                <AccountingRangeDashboardTables />
            </div>
        </>
    );
};

export default AccountingRangeDashboard;
